/* Ultra-Premium Sort Sheet (Glass + Green Gradient + Micro-Interactions)
   - Variant switch: "glass" | "gradient"
   - RTL-ready
   - Haptics (mobile): navigator.vibrate fallback
   - Animations: CSS (no libraries)
*/

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';

const BRAND = {
  green: "#195B2F",
  green2: "#10B981",
  text: "#0B1220",
  muted: "rgba(15, 23, 42, .65)",
  line: "rgba(2, 6, 23, .08)",
};

const SORT_OPTIONS_AR = [
  { key: "newest", label: "الأحدث", labelEn: "Newest", icon: "🕒" },
  { key: "price_asc", label: "السعر: من الأقل للأعلى", labelEn: "Price: Low to High", icon: "↑" },
  { key: "price_desc", label: "السعر: من الأعلى للأقل", labelEn: "Price: High to Low", icon: "↓" },
  { key: "views", label: "الأكثر مشاهدة", labelEn: "Most Viewed", icon: "👁" },
];

// tiny haptic helper
function haptic(type = "light") {
  try {
    const ms = type === "medium" ? 20 : 10;
    if (navigator?.vibrate) navigator.vibrate(ms);
  } catch {}
}

export default function PremiumSortSheet({
  open,
  onClose,
  value,
  onChange,
  onApply,
  onReset,
  direction = "rtl",
  variant = "glass",
  isRTL = true,
}) {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const [activeKey, setActiveKey] = useState(value || "newest");

  useEffect(() => {
    if (open) {
      setMounted(true);
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => (document.body.style.overflow = prev);
    } else {
      const t = setTimeout(() => setMounted(false), 220);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (value) setActiveKey(value);
  }, [value]);

  const sheetClass = useMemo(() => {
    const base = "ws-sheet";
    return `${base} ${variant === "gradient" ? "ws-sheet--gradient" : "ws-sheet--glass"} ${
      open ? "ws-open" : "ws-closed"
    }`;
  }, [open, variant]);

  if (!mounted) return null;

  const handlePick = (key) => {
    setActiveKey(key);
    onChange?.(key);
    haptic("light");
  };

  const apply = () => {
    haptic("medium");
    onApply?.(activeKey);
    onClose?.();
  };

  const reset = () => {
    haptic("light");
    const k = "newest";
    setActiveKey(k);
    onReset?.();
    onChange?.(k);
  };

  return (
    <>
      <style>{styles}</style>

      <div 
        className={`ws-backdrop ${open ? "ws-open" : "ws-closed"}`} 
        onClick={onClose} 
      />

      <div 
        className={sheetClass} 
        dir={direction} 
        role="dialog" 
        aria-modal="true"
        data-testid="sort-sheet"
      >
        <div className="ws-grabber" />
        <div className="ws-header">
          <div className="ws-title">{t('pss.sortBy')}</div>
          <button 
            className="ws-close" 
            onClick={onClose} 
            aria-label="Close"
            data-testid="sort-sheet-close"
          >
            ✕
          </button>
        </div>

        <div className="ws-list">
          {SORT_OPTIONS_AR.map((opt) => {
            const selected = opt.key === activeKey;
            return (
              <button
                key={opt.key}
                className={`ws-row ${selected ? "ws-selected" : ""}`}
                onClick={() => handlePick(opt.key)}
                data-testid={`sort-option-${opt.key}`}
              >
                <div className="ws-left">
                  <span className="ws-ic">{opt.icon}</span>
                  <span className="ws-label">{isRTL ? opt.label : opt.labelEn}</span>
                </div>

                <div className="ws-right">
                  <span className={`ws-check ${selected ? "ws-check--on" : ""}`}>✓</span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="ws-actions">
          <button 
            className="ws-apply" 
            onClick={apply}
            data-testid="sort-apply-btn"
          >
            {t('pss.apply')}
          </button>
          <button 
            className="ws-reset" 
            onClick={reset}
            data-testid="sort-reset-btn"
          >
            {t('pss.clearAll')}
          </button>
        </div>

        <div className="ws-safe" />
      </div>
    </>
  );
}

const styles = `
/* BACKDROP */
.ws-backdrop{
  position: fixed; inset: 0;
  background: rgba(2,6,23,.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  transition: opacity 220ms ease;
  z-index: 9998;
}
.ws-backdrop.ws-open{ opacity: 1; pointer-events: auto; }
.ws-backdrop.ws-closed{ opacity: 0; pointer-events: none; }

/* SHEET BASE */
.ws-sheet{
  position: fixed;
  left: 0; right: 0; bottom: 0;
  z-index: 9999;
  border-radius: 24px 24px 0 0;
  transform: translateY(110%);
  transition: transform 300ms cubic-bezier(.2,.8,.2,1);
  padding: 10px 14px 0;
  box-shadow: 0 -20px 60px rgba(0,0,0,.25);
  border: 1px solid rgba(255,255,255,.22);
  border-bottom: none;
  overflow: hidden;
  will-change: transform;
}
.ws-sheet.ws-open{ transform: translateY(0%); }
.ws-sheet.ws-closed{ transform: translateY(110%); pointer-events: none; }

/* VARIANTS */
.ws-sheet--glass{
  background: rgba(255,255,255,.92);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}
html.dark .ws-sheet--glass{
  background: rgba(30, 41, 59, 0.95);
  border-color: rgba(255,255,255,.08);
}

.ws-sheet--gradient{
  background: linear-gradient(180deg,
    rgba(22,122,74,.12) 0%,
    rgba(16,185,129,.06) 35%,
    rgba(255,255,255,.96) 100%
  );
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}
html.dark .ws-sheet--gradient{
  background: linear-gradient(180deg,
    rgba(22,122,74,.18) 0%,
    rgba(16,185,129,.10) 35%,
    rgba(30, 41, 59, 0.95) 100%
  );
}

/* GRABBER */
.ws-grabber{
  width: 44px; height: 5px;
  border-radius: 999px;
  margin: 6px auto 10px;
  background: rgba(15,23,42,.18);
}
html.dark .ws-grabber{
  background: rgba(255,255,255,.25);
}

/* HEADER */
.ws-header{
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px;
  padding: 2px 4px 10px;
}
.ws-title{
  font-size: 18px;
  font-weight: 700;
  color: #0B1220;
  letter-spacing: .2px;
}
html.dark .ws-title{
  color: #f1f5f9;
}

.ws-close{
  width: 40px; height: 40px;
  border-radius: 999px;
  border: 1px solid rgba(2,6,23,.08);
  background: rgba(255,255,255,.65);
  color: rgba(2,6,23,.7);
  display: grid; place-items: center;
  cursor: pointer;
  font-size: 16px;
  transition: transform 120ms ease, background 180ms ease;
}
html.dark .ws-close{
  background: rgba(51, 65, 85, 0.8);
  border-color: rgba(255,255,255,.1);
  color: rgba(255,255,255,.7);
}
.ws-close:active{ transform: scale(.96); }

/* LIST */
.ws-list{
  display: grid;
  gap: 8px;
  padding: 6px 2px 14px;
}
.ws-row{
  height: 52px;
  border-radius: 14px;
  border: 1px solid rgba(2,6,23,.06);
  background: rgba(255,255,255,.70);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  cursor: pointer;
  transform: translateZ(0);
  transition: transform 140ms ease, box-shadow 200ms ease, background 200ms ease, border-color 200ms ease;
  position: relative;
  overflow: hidden;
}
html.dark .ws-row{
  background: rgba(51, 65, 85, 0.5);
  border-color: rgba(255,255,255,.06);
}

.ws-row::before{
  content: "";
  position: absolute;
  top: -60%; left: -40%;
  width: 60%; height: 220%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.5), transparent);
  transform: rotate(18deg) translateX(-120%);
  transition: transform 520ms ease;
  pointer-events: none;
}
.ws-row:hover::before{ transform: rotate(18deg) translateX(220%); }
.ws-row:active{
  transform: scale(.98);
}

.ws-left{
  display: flex; align-items: center; gap: 10px;
  min-width: 0;
}
.ws-ic{
  width: 28px; height: 28px;
  border-radius: 10px;
  display: grid; place-items: center;
  background: rgba(22,122,74,.08);
  border: 1px solid rgba(22,122,74,.12);
  font-size: 14px;
}
html.dark .ws-ic{
  background: rgba(22,122,74,.15);
  border-color: rgba(22,122,74,.22);
}

.ws-label{
  font-size: 16px;
  font-weight: 600;
  color: rgba(2,6,23,.88);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70vw;
}
html.dark .ws-label{
  color: rgba(255,255,255,.88);
}

.ws-right{
  display: flex; align-items: center; justify-content: center;
}
.ws-check{
  width: 24px; height: 24px;
  border-radius: 999px;
  display: grid; place-items: center;
  font-size: 14px;
  color: transparent;
  border: 1px solid rgba(2,6,23,.08);
  background: rgba(255,255,255,.55);
  transform: scale(.9);
  transition: transform 160ms ease, color 160ms ease, background 160ms ease, box-shadow 200ms ease, border-color 200ms ease;
}
html.dark .ws-check{
  background: rgba(51, 65, 85, 0.6);
  border-color: rgba(255,255,255,.1);
}

/* SELECTED STATE */
.ws-selected{
  background: rgba(22,122,74,.08);
  border-color: rgba(22,122,74,.18);
  box-shadow:
    0 6px 20px rgba(22,122,74,.12),
    0 0 0 1px rgba(16,185,129,.08) inset;
}
html.dark .ws-selected{
  background: rgba(22,122,74,.18);
  border-color: rgba(22,122,74,.28);
}

.ws-selected .ws-ic{
  background: rgba(22,122,74,.14);
  border-color: rgba(22,122,74,.22);
}
.ws-check--on{
  color: #195B2F;
  border-color: rgba(22,122,74,.25);
  background: rgba(22,122,74,.10);
  box-shadow: 0 4px 12px rgba(22,122,74,.15);
  transform: scale(1);
}
html.dark .ws-check--on{
  color: #10B981;
  background: rgba(16,185,129,.18);
  border-color: rgba(16,185,129,.30);
}

/* ACTIONS */
.ws-actions{
  padding: 2px 2px 14px;
  display: grid;
  gap: 10px;
}
.ws-apply{
  height: 48px;
  border-radius: 14px;
  border: none;
  color: white;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  background: linear-gradient(135deg, #195B2F 0%, #10B981 100%);
  box-shadow: 0 10px 24px rgba(22,122,74,.20);
  transition: transform 140ms ease, filter 180ms ease;
}
.ws-apply:active{ transform: scale(.99); filter: brightness(.96); }

.ws-reset{
  height: 42px;
  border-radius: 12px;
  border: 1px solid rgba(2,6,23,.08);
  background: rgba(255,255,255,.60);
  color: rgba(2,6,23,.65);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: transform 140ms ease, background 180ms ease;
}
html.dark .ws-reset{
  background: rgba(51, 65, 85, 0.5);
  border-color: rgba(255,255,255,.08);
  color: rgba(255,255,255,.65);
}
.ws-reset:active{ transform: scale(.985); }

.ws-safe{ height: env(safe-area-inset-bottom, 12px); }

/* Reduced motion support */
@media (prefers-reduced-motion: reduce){
  .ws-sheet, .ws-backdrop, .ws-row, .ws-check, .ws-close, .ws-apply, .ws-reset{ transition: none !important; }
  .ws-row::before{ display: none; }
}
`;

export { PremiumSortSheet };
